import { registerPlugin } from "@capacitor/core";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  ForegroundService,
  Importance,
  ServiceType,
} from "@capawesome-team/capacitor-android-foreground-service";
import type {
  LiveRecorderStatusHelpers,
  NativeLiveRecordingPlugin,
  RecordingHealthStatus,
  StreamStatus,
} from "./useLiveRecorder";
import { useNativeLiveRecorder } from "./useLiveRecorder";

export interface LiveRecordingPlugin extends NativeLiveRecordingPlugin {
  updateForegroundRecordingNotification(options: {
    title: string;
    body: string;
  }): Promise<{ updated: boolean }>;
}

const LiveRecording = registerPlugin<LiveRecordingPlugin>("LiveRecording");

const foregroundServiceNotificationId = 12343;
const foregroundServiceChannelId = "live_recording";
const reliabilityNotificationId = 12344;

const FGS_RETRY_STATUSES = ["recording", "uploading", "warning", "paused"];

export const useLiveRecorderAndroid = () => {
  const { t } = useTranslation();
  const foregroundServiceStartedRef = useRef(false);
  const foregroundServiceStartPromiseRef = useRef<Promise<void> | null>(null);
  const foregroundServiceFailedRef = useRef(false);
  const lastNotifiedHealthRef = useRef<RecordingHealthStatus>("healthy");

  const ensureForegroundServicePermission = async (
    shouldPrompt: boolean,
  ): Promise<boolean> => {
    try {
      const permission = await ForegroundService.checkPermissions();
      if (permission.display === "granted") {
        return true;
      }

      if (shouldPrompt && permission.display.includes("prompt")) {
        const requestedPermission =
          await ForegroundService.requestPermissions();
        return requestedPermission.display === "granted";
      }

      return false;
    } catch (error) {
      console.error("Failed to verify foreground service permissions:", error);
      return false;
    }
  };

  const ensureForegroundServiceChannel = async () => {
    try {
      await ForegroundService.createNotificationChannel({
        id: foregroundServiceChannelId,
        name: t("useLiveRecorder.recordingInProgress") || "Recording",
        description:
          t("useLiveRecorder.recordingInBackground") ||
          "Recording in background",
        importance: Importance.Low,
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      if (!errorMessage.toLowerCase().includes("unavailable")) {
        throw error;
      }
    }
  };

  const startForegroundService = async (options?: {
    shouldPrompt?: boolean;
  }) => {
    if (foregroundServiceStartedRef.current) {
      return;
    }

    if (foregroundServiceStartPromiseRef.current) {
      await foregroundServiceStartPromiseRef.current;
      return;
    }

    // The start promise must complete (or throw) before we mark the service as
    // started - otherwise a permission denial leaves the flag in an inconsistent
    // state and a later call would early-return as if the service were running.
    const startPromise = (async () => {
      const permissionGranted = await ensureForegroundServicePermission(
        options?.shouldPrompt ?? true,
      );
      if (!permissionGranted) {
        throw new Error(
          "Foreground service notification permission is not granted.",
        );
      }

      await ensureForegroundServiceChannel();

      await ForegroundService.startForegroundService({
        title: t("useLiveRecorder.recordingInProgress") || "Recording...",
        body:
          t("useLiveRecorder.recordingInBackground") ||
          "Recording in background",
        id: foregroundServiceNotificationId,
        smallIcon: "ic_stat_notify",
        notificationChannelId: foregroundServiceChannelId,
        serviceType: ServiceType.Microphone,
      });

      foregroundServiceStartedRef.current = true;
    })();

    // The tracked copy exists only so concurrent callers can wait for the
    // in-flight start; swallow its rejection so a failed start doesn't also
    // surface as an unhandled promise rejection (the caller below still gets
    // the error from awaiting startPromise directly).
    foregroundServiceStartPromiseRef.current = startPromise
      .catch(() => undefined)
      .finally(() => {
        foregroundServiceStartPromiseRef.current = null;
      });

    // Propagate the error to the caller so a failed start aborts the recording
    // session instead of letting it continue without the foreground service
    // (Android would silently kill the recording on backgrounding).
    await startPromise;
  };

  const stopForegroundService = async () => {
    if (foregroundServiceStartPromiseRef.current) {
      await foregroundServiceStartPromiseRef.current;
    }

    foregroundServiceFailedRef.current = false;
    lastNotifiedHealthRef.current = "healthy";

    try {
      // Always call the native stop. The service may have been started even if
      // a later notification probe was inconclusive, and Capawesome also owns
      // a wake lock that must be released. Stopping a missing service is safe.
      await ForegroundService.stopForegroundService();
    } catch (error) {
      console.error("Failed to stop foreground service:", error);
    } finally {
      foregroundServiceStartedRef.current = false;
    }
  };

  // Keep the foreground-service notification truthful: while the recorder is
  // unhealthy it must not keep saying "Recording...".
  const updateForegroundNotificationForHealth = async (info: StreamStatus) => {
    if (!foregroundServiceStartedRef.current || !info.healthStatus) {
      return;
    }

    if (info.healthStatus === lastNotifiedHealthRef.current) {
      return;
    }
    const isHealthy = info.healthStatus === "healthy";
    try {
      const result = await LiveRecording.updateForegroundRecordingNotification({
        title: isHealthy
          ? t("useLiveRecorder.recordingInProgress") || "Recording..."
          : t("useLiveRecorder.recordingNeedsAttention") ||
            "⚠ Recording needs attention",
        body: isHealthy
          ? t("useLiveRecorder.recordingInBackground") ||
            "Recording in background"
          : info.lastErrorMessage ||
            info.message ||
            t("useLiveRecorder.recordingNeedsAttention") ||
            "Recording needs attention",
      });

      if (!result.updated) {
        // Do not clear foregroundServiceStartedRef here. A just-started service
        // can take a moment to publish its notification, and cleanup must still
        // release Capawesome's wake lock. Mark it for reconciliation only when
        // the app is foregrounded, where a microphone FGS restart is permitted.
        foregroundServiceFailedRef.current = true;
        return;
      }

      foregroundServiceFailedRef.current = false;
      lastNotifiedHealthRef.current = info.healthStatus;
    } catch (error) {
      console.error("Failed to update foreground service notification:", error);
    }
  };

  return useNativeLiveRecorder({
    platform: "android",
    plugin: LiveRecording,
    reliabilityNotificationId,
    audioLevelThrottleMs: 80,
    onBeforeStart: async () => {
      foregroundServiceStartedRef.current = false;
      foregroundServiceFailedRef.current = false;
      lastNotifiedHealthRef.current = "healthy";
      // Don't start the foreground service here. Android requires microphone
      // access to be active before starting a ServiceType.Microphone service.
      // The service is started from onStatusChange once the native plugin
      // confirms recording has begun and the mic is open.
    },
    onCleanup: stopForegroundService,
    onAppActive: async ({ status, helpers }) => {
      // A previous foreground-service start may have failed because the app
      // was backgrounded during the bridge round-trip (Android 14+ forbids
      // starting a mic-type FGS from the background) or notifications were
      // denied. Now that the app is in the foreground again, retry so the
      // rest of the session gets background protection.
      if (
        !foregroundServiceFailedRef.current ||
        !FGS_RETRY_STATUSES.includes(status)
      ) {
        return;
      }

      // The shared resume reconciliation runs across the Capacitor bridge too.
      // Check the engine here before replacing an uncertain service so the two
      // async resume paths cannot leave a fresh "Recording" notification behind
      // after the native capture engine has already died. Uploading is different:
      // capture is expected to be stopped while queued chunks finish draining.
      if (status !== "uploading") {
        try {
          const engineState = await LiveRecording.getEngineState?.();
          if (engineState && !engineState.isEngineRunning) {
            await stopForegroundService();
            return;
          }
        } catch (error) {
          // A transient state-probe failure must not prevent restoring background
          // protection for a recording that is still running.
          console.error(
            "Failed to verify recording engine before foreground service retry:",
            error,
          );
        }
      }

      try {
        // startForegroundService() normally short-circuits when JS believes the
        // service is active. A failed native notification probe means its state
        // is uncertain, so force a native stop/start reconciliation now that the
        // app is foregrounded. Capawesome's start call releases any old wake lock.
        foregroundServiceStartedRef.current = false;
        await startForegroundService({ shouldPrompt: false });
        foregroundServiceFailedRef.current = false;
        helpers.setHealthWarning(null);
      } catch (error) {
        console.error("Foreground service retry failed:", error);
      }
    },
    onStatusChange: async (
      info: StreamStatus,
      helpers: LiveRecorderStatusHelpers,
    ) => {
      // Start the foreground service once the native plugin confirms the mic
      // is active. This must happen AFTER startStream() has opened the mic —
      // Android requires active microphone access before a Microphone-type
      // foreground service can be started. Also handles the recovery-resume
      // path (status "uploading") where we still need the service to keep the
      // process alive while chunks drain.
      if (
        ["recording", "uploading"].includes(info.status) &&
        !foregroundServiceStartedRef.current
      ) {
        try {
          await startForegroundService({
            shouldPrompt: info.status === "recording",
          });
          foregroundServiceFailedRef.current = false;
        } catch (error) {
          // Don't abort the recording — it works fine in the foreground. But
          // the user must know backgrounding will kill it, so surface the
          // failure on recordingHealth instead of only logging it. The start
          // is retried on the next appStateChange → active.
          console.error(
            "Foreground service unavailable; recording will continue without background protection:",
            error,
          );
          foregroundServiceFailedRef.current = true;
          helpers.setHealthWarning(
            t("useLiveRecorder.backgroundRecordingUnavailable") ||
              "Background recording is unavailable — keep the app open while recording.",
          );
        }
      }

      await updateForegroundNotificationForHealth(info);
    },
  });
};
